import { setDefaultDashboard } from 'services/defaultDashboards';

import { Dashboard } from 'types/dashboard';
import { Account } from 'types/account';

import { APIObject } from 'services/api';
import { listDashboardTemplates } from 'dashboards/hooks/useDashboardTemplates';
import { readDashboardTemplate } from 'dashboards/hooks/useDashboardTemplate';

const RING_DASHBOARD_TEMPLATE_NAMES = [
  'Ring Occupancy',
  'Ring Demographics',
  'Ring Overview', // last so this will appear first in the list.
];

export async function setupRingAccount(
  api: APIObject,
  user: Account,
  applicationId: string
): Promise<boolean> {
  const dashboardTemplates = await listDashboardTemplates(
    api.client,
    applicationId,
    {
      only_included_in_plan: true,
    }
  );
  const dashboardTemplateByName = new Map(
    dashboardTemplates.map((template) => [template.name, template])
  );

  const dashboards: Dashboard[] = [];

  for (const dashboardTemplateName of RING_DASHBOARD_TEMPLATE_NAMES) {
    const dashboardTemplateSummary = dashboardTemplateByName.get(
      dashboardTemplateName
    );

    if (!dashboardTemplateSummary) {
      throw new Error(
        `Failed to find ${dashboardTemplateName} dashboard template`
      );
    }

    const dashboardTemplate = await readDashboardTemplate(
      api.client,
      applicationId,
      dashboardTemplateSummary.id
    );

    const dashboard: Dashboard | undefined =
      await api.applications.dashboards.create(
        applicationId,
        dashboardTemplate.name,
        dashboardTemplate.configuration
      );

    if (dashboard) {
      dashboards.push(dashboard);
    }
  }

  if (!dashboards.length) {
    throw new Error('Failed to create default dashboards');
  }

  // Set the last created dashboard as default for this application
  const defaultDashboard = dashboards[dashboards.length - 1];
  const currentSerialized = user.account_attributes?.default_dashboards;
  const updatedSerialized = setDefaultDashboard(
    currentSerialized,
    applicationId,
    defaultDashboard.id
  );

  await api.accounts.update({
    id: user.id,
    account_attributes: {
      ...user.account_attributes,
      default_dashboards: updatedSerialized,
    },
  });

  return true;
}
